// src/components/programs/ProgramForm/Step2Sections/GlobalDiscountSection.jsx

import React, { useEffect } from "react";
import { Grid, Box, Alert, Typography, Stack, alpha, useTheme, Chip } from "@mui/material";
import {
    InfoOutlined as InfoIcon,
    DiscountOutlined as DiscountIcon,
    CheckCircleOutline as CheckIcon,
} from "@mui/icons-material";
import FormInput from "../../../inputs/FormInput";

const GlobalDiscountSection = ({
    globalDiscount,
    onDiscountChange,
    errors,
    isArabic,
}) => {
    const theme = useTheme();

    const discountTypeOptions = [
        { id: "zero", name: isArabic ? "بدون خصم" : "No Discount" },
        { id: "amount", name: isArabic ? "مبلغ ثابت" : "Fixed Amount" },
        { id: "percent", name: isArabic ? "نسبة مئوية" : "Percentage" },
    ];

    const discountTargetOptions = [
        { id: "none", name: isArabic ? "لا يوجد" : "None" },
        { id: "sell", name: isArabic ? "سعر البيع فقط" : "Sell Price Only" },
        { id: "cost", name: isArabic ? "سعر التكلفة فقط" : "Cost Price Only" },
        { id: "both", name: isArabic ? "كلاهما" : "Both" },
    ];

    // Reset values when "No Discount" is selected
    useEffect(() => {
        if (globalDiscount?.type === "zero") {
            if (globalDiscount?.value !== 0) {
                onDiscountChange("value", 0);
            }
            if (globalDiscount?.target !== "none") {
                onDiscountChange("target", "none");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalDiscount?.type]);

    return (
        <Box>
            {/* Compact Header with Info */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <DiscountIcon sx={{ fontSize: 18, color: 'info.main' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                        {isArabic ? "الخصم الشامل" : "Global Discount"}
                    </Typography>
                </Box>
            </Stack>

            {/* Compact Info Alert */}
            <Alert
                severity="info"
                icon={<InfoIcon sx={{ fontSize: 18 }} />}
                sx={{
                    mb: 2,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    '& .MuiAlert-icon': {
                        fontSize: 18,
                        mr: 1,
                    },
                    '& .MuiAlert-message': {
                        padding: 0,
                        overflow: 'hidden', textOverflow: 'ellipsis'
                    },
                }}
            >
                <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {isArabic
                        ? "الخصم الشامل يُطبق على الإجمالي النهائي بعد حساب جميع المنتجات"
                        : "Global discount is applied to the final total after calculating all products"}
                </Typography>
            </Alert>

            <Grid container spacing={1.5}>
                {/* Discount Type */}
                <Grid item xs={12}>
                    <FormInput
                        label={isArabic ? "نوع الخصم" : "Discount Type"}
                        type="select"
                        name="global_discount_type"
                        value={globalDiscount?.type || "zero"}
                        onChange={(e) => onDiscountChange("type", e.target.value)}
                        options={discountTypeOptions}
                        required
                        size="small"
                    />
                </Grid>

                {/* Show additional fields only if discount type is not zero */}
                {globalDiscount?.type !== "zero" && (
                    <>
                        {/* Discount Target */}
                        <Grid item xs={12}>
                            <FormInput
                                label={isArabic ? "تطبيق الخصم على" : "Apply Discount On"}
                                type="select"
                                name="global_discount_target"
                                value={globalDiscount?.target || "none"}
                                onChange={(e) => onDiscountChange("target", e.target.value)}
                                options={discountTargetOptions}
                                required
                                size="small"
                            />
                        </Grid>

                        {/* Discount Value - Only show if target is selected */}
                        {globalDiscount?.target && globalDiscount?.target !== "none" && (
                            <>
                                <Grid item xs={12}>
                                    <FormInput
                                        label={
                                            isArabic
                                                ? globalDiscount?.type === "percent"
                                                    ? "قيمة الخصم (%)"
                                                    : "قيمة الخصم"
                                                : globalDiscount?.type === "percent"
                                                    ? "Discount Value (%)"
                                                    : "Discount Value"
                                        }
                                        type="number"
                                        name="global_discount_value"
                                        placeholder={
                                            isArabic
                                                ? "أدخل قيمة الخصم"
                                                : "Enter discount value"
                                        }
                                        value={globalDiscount?.value || ""}
                                        onChange={(e) => onDiscountChange("value", parseFloat(e.target.value) || 0)}
                                        error={!!errors.global_discount}
                                        helperText={errors.global_discount}
                                        inputProps={{
                                            min: 0,
                                            max: globalDiscount?.type === "percent" ? 100 : undefined,
                                            step: globalDiscount?.type === "percent" ? 1 : 0.01,
                                        }}
                                        required
                                        size="small"
                                    />
                                </Grid>

                                {/* Compact Success Indicator */}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: alpha(theme.palette.success.main, 0.05),
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                        }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                            <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4, flex: 1 }}>
                                                {globalDiscount?.target === "sell" && (
                                                    isArabic
                                                        ? "سيتم تطبيق الخصم على سعر البيع فقط"
                                                        : "Discount will be applied to sell price only"
                                                )}
                                                {globalDiscount?.target === "cost" && (
                                                    isArabic
                                                        ? "سيتم تطبيق الخصم على سعر التكلفة فقط"
                                                        : "Discount will be applied to cost price only"
                                                )}
                                                {globalDiscount?.target === "both" && (
                                                    isArabic
                                                        ? "سيتم تطبيق الخصم على سعر البيع والتكلفة"
                                                        : "Discount will be applied to both prices"
                                                )}
                                            </Typography>
                                            <Chip
                                                label={
                                                    globalDiscount?.target === "sell"
                                                        ? (isArabic ? "بيع" : "Sell")
                                                        : globalDiscount?.target === "cost"
                                                            ? (isArabic ? "تكلفة" : "Cost")
                                                            : (isArabic ? "كلاهما" : "Both")
                                                }
                                                size="small"
                                                color="success"
                                                sx={{
                                                    height: 22,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                </Grid>
                            </>
                        )}
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default GlobalDiscountSection;
